/**
 * <product-picker> — a self-contained storefront product/variant picker.
 *
 * Lets a shopper search the store, pick a real product, choose a specific
 * variant, and see live in-stock / out-of-stock status. It knows nothing about
 * "returns" or any host page: it only resolves a real variant (or enrolls an
 * out-of-stock one for back-in-stock notification) and emits the result.
 *
 * Contract
 *   Input (dataset):
 *     data-scenario        — opaque tag, forwarded to back-in-stock enroll
 *     data-context-idx     — caller-defined key, echoed back on every event
 *     data-prefill-handle  — optional: open straight into a product's specs
 *   Output (bubbling CustomEvents):
 *     enroute:product-picked   { variantId, productId, title, variantTitle,
 *                                sku, price, available, contextIdx }
 *     enroute:product-enrolled { variantId, productId, contextIdx }
 *
 * Reuses the storefront pattern from assets/search.js (public Storefront token,
 * shipped in the theme) and BackInStockModal from assets/back-in-stock-modal.js.
 */
(function () {
  // Endpoint + public token come from snippets/storefront-api.liquid, rendered in <head>.
  const STOREFRONT = window.EnrouteStorefront || {};
  const SEARCH_DEBOUNCE_MS = 300;
  const SEARCH_LIMIT = 8;

  /** Country/language for @inContext, mirroring assets/search.js. */
  function storefrontContext() {
    return {
      country: (window.Shopify?.country || "US").toUpperCase(),
      language: (window.Shopify?.locale || "en").toUpperCase().replace("-", "_"),
    };
  }

  async function storefrontFetch(query, variables, signal) {
    const response = await fetch(STOREFRONT.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT.token,
      },
      body: JSON.stringify({
        query,
        variables: { ...variables, ...storefrontContext() },
      }),
      signal,
    });
    const { data, errors } = await response.json();
    if (errors?.length) throw new Error(errors[0].message);
    return data;
  }

  const SEARCH_QUERY = `
    query PickerSearch($query: String!, $first: Int!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
      search(query: $query, first: $first, types: [PRODUCT], unavailableProducts: SHOW) {
        edges {
          node {
            ... on Product {
              id
              handle
              title
              isGiftCard
              tags
              featuredImage {
                url(transform: { maxWidth: 96, maxHeight: 96, crop: CENTER })
              }
              priceRange { minVariantPrice { amount currencyCode } }
            }
          }
        }
      }
    }`;

  const PRODUCT_QUERY = `
    query PickerProduct($handle: String!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
      product(handle: $handle) {
        id
        handle
        title
        isGiftCard
        tags
        options { name values }
        variants(first: 100) {
          nodes {
            id
            title
            availableForSale
            sku
            price { amount currencyCode }
            selectedOptions { name value }
            image { url }
          }
        }
      }
    }`;

  /** Default English copy; overridden per-locale via #product-picker-locale JSON. */
  const DEFAULT_LOCALE = {
    search_placeholder: "Search for a product to exchange for…",
    searching: "Searching…",
    no_results: "No products found. Try another keyword.",
    error: "Something went wrong. Please try again.",
    change_product: "Change product",
    select_option: "Choose {{ name }}",
    in_stock: "In stock",
    out_of_stock: "Out of stock",
    notify_me: "Notify me when it's back",
    picked: "Selected for exchange",
    enrolled: "We'll email you when it's back in stock.",
  };

  /** "Ring Size: 7 / Color: Gold"; "" for a lone Default-Title option. */
  function formatVariantTitle(selectedOptions) {
    const opts = selectedOptions || [];
    if (opts.length === 0) return "";
    if (opts.length === 1 && opts[0].name === "Title" && opts[0].value === "Default Title") {
      return "";
    }
    return opts.map((o) => `${o.name}: ${o.value}`).join(" / ");
  }

  /** A gift card or customized product can never be an exchange target. */
  function isExcludedProduct(product) {
    if (!product) return true;
    if (product.isGiftCard) return true;
    const tags = product.tags || [];
    return tags.some((t) => String(t).trim().toLowerCase() === "customized");
  }

  class ProductPicker extends HTMLElement {
    constructor() {
      super();
      this._abort = null;
      this._debounceTimer = null;
      this._locale = DEFAULT_LOCALE;
      this._product = null; // { id, handle, title, options, variants }
      this._selection = {}; // { [optionName]: value }
      this._onInput = this._onInput.bind(this);
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      this._scenario = this.dataset.scenario || "";
      this._contextIdx = this.dataset.contextIdx || "";
      this._locale = this._readLocale();
      this._renderSearch();
      this.addEventListener("input", this._onInput);
      this.addEventListener("click", this._onClick);

      const prefill = this.dataset.prefillHandle;
      if (prefill) this._loadProduct(prefill);
    }

    disconnectedCallback() {
      this.removeEventListener("input", this._onInput);
      this.removeEventListener("click", this._onClick);
      this._cancelInFlight();
      clearTimeout(this._debounceTimer);
    }

    /** Merge injected locale (if any) over the English defaults. */
    _readLocale() {
      try {
        const el = document.getElementById("product-picker-locale");
        if (!el) return DEFAULT_LOCALE;
        return { ...DEFAULT_LOCALE, ...JSON.parse(el.textContent) };
      } catch {
        return DEFAULT_LOCALE;
      }
    }

    _t(key, vars) {
      let s = this._locale[key] || DEFAULT_LOCALE[key] || key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          s = s.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, "g"), v);
        }
      }
      return s;
    }

    _cancelInFlight() {
      if (this._abort) {
        this._abort.abort();
        this._abort = null;
      }
    }

    // ---- Search ----------------------------------------------------------

    _onInput(event) {
      const input = event.target.closest("[data-picker-input]");
      if (!input) return;
      const query = input.value.trim();
      clearTimeout(this._debounceTimer);
      if (query.length < 2) {
        this._renderResults([]);
        return;
      }
      this._debounceTimer = setTimeout(() => this._runSearch(query), SEARCH_DEBOUNCE_MS);
    }

    async _runSearch(query) {
      this._cancelInFlight();
      this._abort = new AbortController();
      this._setStatus(this._t("searching"));
      try {
        const data = await storefrontFetch(
          SEARCH_QUERY,
          { query, first: SEARCH_LIMIT },
          this._abort.signal,
        );
        const products = (data?.search?.edges || [])
          .map((e) => e.node)
          .filter((p) => p && !isExcludedProduct(p));
        this._renderResults(products);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("product-picker search failed:", err);
        this._setStatus(this._t("error"));
      }
    }

    // ---- Product + variant selection ------------------------------------

    async _loadProduct(handle) {
      this._cancelInFlight();
      this._abort = new AbortController();
      this._setStatus(this._t("searching"));
      try {
        const data = await storefrontFetch(
          PRODUCT_QUERY,
          { handle },
          this._abort.signal,
        );
        const product = data?.product;
        if (!product || isExcludedProduct(product)) {
          this._setStatus(this._t("no_results"));
          return;
        }
        this._product = product;
        this._selection = {};
        this._renderProduct();
        // A single Default-Title product has no options to choose — auto-select
        // its only variant and emit straight away.
        if (this._isNoOptionProduct(product)) {
          for (const opt of product.options) this._selection[opt.name] = opt.values[0];
          this._renderProduct();
          this._onVariantResolved();
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("product-picker product load failed:", err);
        this._setStatus(this._t("error"));
      }
    }

    _isNoOptionProduct(product) {
      const opts = product.options || [];
      return (
        opts.length === 1 &&
        opts[0].name === "Title" &&
        opts[0].values.length === 1 &&
        opts[0].values[0] === "Default Title"
      );
    }

    /** Variant matching the current full selection, or null if incomplete. */
    _resolveVariant() {
      const product = this._product;
      if (!product) return null;
      const optionNames = (product.options || []).map((o) => o.name);
      if (optionNames.some((name) => !this._selection[name])) return null;
      return (
        (product.variants?.nodes || []).find((v) =>
          (v.selectedOptions || []).every(
            (o) => this._selection[o.name] === o.value,
          ),
        ) || null
      );
    }

    _onClick(event) {
      const resultBtn = event.target.closest("[data-picker-result]");
      if (resultBtn) {
        event.preventDefault();
        this._loadProduct(resultBtn.getAttribute("data-picker-result"));
        return;
      }

      const changeBtn = event.target.closest("[data-picker-change]");
      if (changeBtn) {
        event.preventDefault();
        this._product = null;
        this._selection = {};
        this._renderSearch();
        return;
      }

      const optionBtn = event.target.closest("[data-picker-option]");
      if (optionBtn) {
        event.preventDefault();
        const name = optionBtn.getAttribute("data-picker-option");
        const value = optionBtn.getAttribute("data-picker-value");
        this._selection[name] = value;
        this._renderProduct();
        this._onVariantResolved();
        return;
      }

      const notifyBtn = event.target.closest("[data-picker-notify]");
      if (notifyBtn) {
        event.preventDefault();
        this._openBackInStock();
        return;
      }
    }

    _onVariantResolved() {
      const variant = this._resolveVariant();
      if (!variant || !variant.availableForSale) return;
      this.dispatchEvent(
        new CustomEvent("enroute:product-picked", {
          bubbles: true,
          detail: {
            variantId: variant.id,
            productId: this._product.id,
            title: this._product.title,
            variantTitle: formatVariantTitle(variant.selectedOptions),
            sku: variant.sku || "",
            price: variant.price?.amount || "",
            available: true,
            contextIdx: this._contextIdx,
          },
        }),
      );
    }

    _openBackInStock() {
      const variant = this._resolveVariant();
      if (!variant || typeof BackInStockModal === "undefined") return;
      const productInfo = {
        productId: this._product.id,
        handle: this._product.handle,
        title: this._product.title,
      };
      new BackInStockModal(variant, this, productInfo);
      this.dispatchEvent(
        new CustomEvent("enroute:product-enrolled", {
          bubbles: true,
          detail: {
            variantId: variant.id,
            productId: this._product.id,
            contextIdx: this._contextIdx,
          },
        }),
      );
      this._setStatus(this._t("enrolled"), "enrolled");
    }

    // ---- Rendering -------------------------------------------------------

    _renderSearch() {
      this.innerHTML = `
        <div class="product-picker__search">
          <input
            type="text"
            class="product-picker__input"
            data-picker-input
            placeholder="${this._escape(this._t("search_placeholder"))}"
            autocomplete="off"
          />
        </div>
        <div class="product-picker__results" data-picker-results></div>
        <div class="product-picker__status" data-picker-status></div>
      `;
    }

    _renderResults(products) {
      const box = this.querySelector("[data-picker-results]");
      if (!box) return;
      if (products.length === 0) {
        box.innerHTML = "";
        this._setStatus(this._t("no_results"));
        return;
      }
      this._setStatus("");
      box.innerHTML = products
        .map((p) => {
          const price = p.priceRange?.minVariantPrice;
          const priceLabel = price ? `${price.amount} ${price.currencyCode}` : "";
          const img = p.featuredImage?.url || "";
          return `
            <button type="button" class="product-picker__result" data-picker-result="${this._escape(p.handle)}">
              ${img ? `<img class="product-picker__result-img" src="${this._escape(img)}" alt="" loading="lazy" width="48" height="48">` : ""}
              <span class="product-picker__result-title">${this._escape(p.title)}</span>
              <span class="product-picker__result-price">${this._escape(priceLabel)}</span>
            </button>`;
        })
        .join("");
    }

    _renderProduct() {
      const product = this._product;
      if (!product) return;
      const variant = this._resolveVariant();

      const optionsHtml = (product.options || [])
        .filter((opt) => !this._isNoOptionProduct(product))
        .map((opt) => {
          const buttons = opt.values
            .map((value) => {
              const active = this._selection[opt.name] === value ? " is-active" : "";
              return `<button type="button" class="product-picker__option${active}" data-picker-option="${this._escape(opt.name)}" data-picker-value="${this._escape(value)}">${this._escape(value)}</button>`;
            })
            .join("");
          return `
            <div class="product-picker__option-group">
              <div class="product-picker__option-label">${this._escape(this._t("select_option", { name: opt.name }))}</div>
              <div class="product-picker__option-values">${buttons}</div>
            </div>`;
        })
        .join("");

      this.innerHTML = `
        <div class="product-picker__selected">
          <div class="product-picker__selected-head">
            <span class="product-picker__selected-title">${this._escape(product.title)}</span>
            <button type="button" class="product-picker__change" data-picker-change>${this._escape(this._t("change_product"))}</button>
          </div>
          ${optionsHtml}
          <div class="product-picker__variant-status">${this._variantStatusHtml(variant)}</div>
        </div>
      `;
    }

    _variantStatusHtml(variant) {
      if (!variant) return "";
      const price = variant.price ? `${variant.price.amount} ${variant.price.currencyCode}` : "";
      if (variant.availableForSale) {
        return `
          <div class="product-picker__status product-picker__status--ok">
            <span class="product-picker__badge product-picker__badge--in">${this._escape(this._t("in_stock"))}</span>
            <span class="product-picker__price">${this._escape(price)}</span>
            <span class="product-picker__picked">✓ ${this._escape(this._t("picked"))}</span>
          </div>`;
      }
      return `
        <div class="product-picker__status product-picker__status--oos">
          <span class="product-picker__badge product-picker__badge--out">${this._escape(this._t("out_of_stock"))}</span>
          <button type="button" class="product-picker__notify" data-picker-notify>${this._escape(this._t("notify_me"))}</button>
        </div>`;
    }

    _setStatus(text, modifier) {
      const el = this.querySelector("[data-picker-status]");
      if (!el) return;
      el.textContent = text || "";
      el.className = "product-picker__status" + (modifier ? ` product-picker__status--${modifier}` : "");
    }

    _escape(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }
  }

  if (!window.customElements.get("product-picker")) {
    window.customElements.define("product-picker", ProductPicker);
  }
})();
